import type { Mood, PeopleResponse, PersonResponse } from '@brimstone/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export type MoodFilter = 'good' | 'neutral' | 'bad' | 'unnoted';

export interface PeopleFilters {
  q: string;
  mood: MoodFilter | null;
  characterId: string | null;
}

/** Everyone grouped with or noted (M13). */
export function usePeople(f: PeopleFilters) {
  const p = new URLSearchParams();
  if (f.q.trim()) p.set('q', f.q.trim());
  if (f.mood) p.set('mood', f.mood);
  if (f.characterId) p.set('characterId', f.characterId);
  const query = p.toString();
  return useQuery({
    queryKey: ['people', query],
    queryFn: () => api<PeopleResponse>(`/api/people${query ? `?${query}` : ''}`),
  });
}

export function usePerson(name: string) {
  return useQuery({
    queryKey: ['person', name.toLowerCase()],
    queryFn: () => api<PersonResponse>(`/api/people/${encodeURIComponent(name)}`),
  });
}

/** A note's mood as a status word, and its colour class (DESIGN: danger always with a label). */
export const MOODS: Record<Mood, { word: string; className: string }> = {
  1: { word: 'GOOD', className: 'text-accent' },
  0: { word: 'NEUTRAL', className: 'text-muted' },
  [-1]: { word: 'BAD', className: 'text-danger' },
};

/** The URL of a person's page. */
export const personHref = (name: string) => `/people/${encodeURIComponent(name)}`;
